import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../../data/firebase/config';
import { DOCUMENTS_DB } from '../../env/documents-db';
import { Role } from '../../types/rbac';

export class RoleService {
  private collectionRef = collection(db, DOCUMENTS_DB.ROLES);

  /**
   * Debug: Verifica la conexión y datos básicos
   */
  async debugConnection(): Promise<any> {
    try {
      console.log('🔍 Debug: Verificando conexión con roles...');
      console.log('📁 Colección:', DOCUMENTS_DB.ROLES);

      const snapshot = await getDocs(this.collectionRef);
      console.log('📊 Total documentos encontrados:', snapshot.size);

      const roles = snapshot.docs.map((doc) => {
        const data = doc.data();
        console.log('📄 Documento:', doc.id, data);
        return { id: doc.id, ...data };
      });

      return { success: true, count: snapshot.size, roles };
    } catch (error) {
      console.error('❌ Error en debug:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Error desconocido',
      };
    }
  }

  /**
   * Obtiene todos los roles activos
   */
  async getAllRoles(): Promise<Role[]> {
    try {
      // Simplificamos la consulta para evitar problemas de índices
      const snapshot = await getDocs(this.collectionRef);

      const roles = snapshot.docs
        .map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Role,
        )
        .filter((role) => role.isActive) // Filtrar en el cliente
        .sort((a, b) => a.name.localeCompare(b.name)); // Ordenar en el cliente

      return roles;
    } catch (error) {
      console.error('Error al obtener roles:', error);
      throw new Error('Error al cargar los roles');
    }
  }

  /**
   * Obtiene un rol por ID
   */
  async getRoleById(id: string): Promise<Role | null> {
    try {
      const docRef = doc(this.collectionRef, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        } as Role;
      }

      return null;
    } catch (error) {
      console.error('Error al obtener rol:', error);
      throw new Error('Error al cargar el rol');
    }
  }

  /**
   * Crea un nuevo rol
   */
  async createRole(roleData: Omit<Role, 'id'>): Promise<string> {
    try {
      const newRole = {
        ...roleData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const docRef = await addDoc(this.collectionRef, newRole);
      return docRef.id;
    } catch (error) {
      console.error('Error al crear rol:', error);
      throw new Error('Error al crear el rol');
    }
  }

  /**
   * Actualiza un rol existente
   */
  async updateRole(
    id: string,
    roleData: Partial<Omit<Role, 'id'>>,
  ): Promise<void> {
    try {
      const docRef = doc(this.collectionRef, id);
      await updateDoc(docRef, {
        ...roleData,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error al actualizar rol:', error);
      throw new Error('Error al actualizar el rol');
    }
  }

  /**
   * Elimina un rol (marca como inactivo)
   */
  async deleteRole(id: string): Promise<void> {
    try {
      const docRef = doc(this.collectionRef, id);
      await updateDoc(docRef, {
        isActive: false,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error('Error al eliminar rol:', error);
      throw new Error('Error al eliminar el rol');
    }
  }

  /**
   * Elimina un rol permanentemente
   */
  async deleteRolePermanently(id: string): Promise<void> {
    try {
      const docRef = doc(this.collectionRef, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error al eliminar rol permanentemente:', error);
      throw new Error('Error al eliminar el rol permanentemente');
    }
  }

  /**
   * Verifica si un usuario tiene un permiso específico
   */
  async checkUserPermission(
    userRoles: string[],
    resource: string,
    action: string,
  ): Promise<boolean> {
    try {
      // Obtener todos los roles del usuario
      const rolesQuery = query(
        this.collectionRef,
        where('__name__', 'in', userRoles),
        where('isActive', '==', true),
      );

      const rolesSnapshot = await getDocs(rolesQuery);

      // Verificar si algún rol tiene el permiso requerido
      for (const roleDoc of rolesSnapshot.docs) {
        const role = roleDoc.data() as Role;
        const hasPermission = role.permissions.some(
          (permission) =>
            permission.resource === resource && permission.action === action,
        );

        if (hasPermission) {
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Error al verificar permisos:', error);
      return false;
    }
  }

  /**
   * Obtiene todos los permisos de un usuario basado en sus roles
   */
  async getUserPermissions(
    userRoles: string[],
  ): Promise<{ resource: string; action: string }[]> {
    try {
      if (userRoles.length === 0) {
        return [];
      }

      const rolesQuery = query(
        this.collectionRef,
        where('__name__', 'in', userRoles),
        where('isActive', '==', true),
      );

      const rolesSnapshot = await getDocs(rolesQuery);
      const permissions: { resource: string; action: string }[] = [];

      rolesSnapshot.docs.forEach((roleDoc) => {
        const role = roleDoc.data() as Role;
        permissions.push(...role.permissions);
      });

      // Eliminar duplicados
      const uniquePermissions = permissions.filter(
        (permission, index, array) =>
          array.findIndex(
            (p) =>
              p.resource === permission.resource &&
              p.action === permission.action,
          ) === index,
      );

      return uniquePermissions;
    } catch (error) {
      console.error('Error al obtener permisos de usuario:', error);
      return [];
    }
  }
}
